import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { TranslatableInputText } from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';

import { type ConditionFormQuestion } from './ConditionQuestion';

export type ITextAndSelectConfig = Extract<ConditionFormQuestion['config'], { input: any }>;

export const TextAndSelectConfig = ({
  config,
  onChange,
}: {
  config: ITextAndSelectConfig;
  onChange: (config: ITextAndSelectConfig) => void;
}) => {
  const { t } = useLingui();

  return (
    <Stack gap="0.5rem">
      <Stack gap="0.25rem">
        <Paragraph weight="medium">
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.label">
            Text label and placeholder:
          </Trans>
        </Paragraph>
        <Stack row gap="0.5rem">
          <div style={{ flex: 1 }}>
            <TranslatableInputText
              size="small"
              value={config.input?.label ?? { en: '', fr: '' }}
              onChange={(label) => onChange({ ...config, input: { ...config.input, label } })}
              aria-label={t({
                id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.input-label.aria',
                message: 'Input label',
              })}
              placeholder={t({
                id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.input-label.placeholder',
                message: 'Text label',
              })}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TranslatableInputText
              size="small"
              value={config.input?.placeholder ?? { en: '', fr: '' }}
              onChange={(placeholder) =>
                onChange({ ...config, input: { ...config.input, placeholder } })
              }
              aria-label={t({
                id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.input-placeholder.aria',
                message: 'Input placeholder',
              })}
              placeholder={t({
                id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.input-placeholder.placeholder',
                message: 'Text placeholder',
              })}
            />
          </div>
        </Stack>
      </Stack>
      <Stack gap="0.25rem">
        <Paragraph weight="medium">
          <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-placeholder">
            "Select option" placeholder:
          </Trans>
        </Paragraph>
        <TranslatableInputText
          size="small"
          value={config.select?.placeholder ?? { en: '', fr: '' }}
          onChange={(placeholder) =>
            onChange({ ...config, select: { ...config.select, placeholder } })
          }
          aria-label={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-placeholder.aria',
            message: 'Select option placeholder',
          })}
          placeholder={t({
            id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-placeholder.placeholder',
            message: 'Placeholder of the "Select option" input',
          })}
        />
        <Stack gap="0.25rem">
          <Paragraph weight="medium">
            <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-options">
              Options:
            </Trans>
          </Paragraph>
          <Stack gap="0.25rem">
            {config.select?.options.map((option) => (
              <Stack key={option.value} row alignItems="center" gap="0.5rem">
                <div style={{ flex: 1 }}>
                  <TranslatableInputText
                    size="small"
                    value={option.label}
                    onChange={(label) =>
                      onChange({
                        ...config,
                        select: {
                          ...config.select,
                          options: config.select?.options.map((o) =>
                            o.value === option.value ? { ...o, label } : o
                          ),
                        },
                      })
                    }
                    aria-label={t({
                      id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-options.option-label.aria',
                      message: 'Option label',
                    })}
                    placeholder={t({
                      id: 'settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-options.option-label.placeholder',
                      message: 'Option label',
                    })}
                  />
                </div>
                <Button
                  variant="secondary-danger"
                  size="medium"
                  iconOnly
                  onPress={() => {
                    onChange({
                      ...config,
                      select: {
                        ...config.select,
                        options: config.select?.options.filter((o) => o.value !== option.value),
                      },
                    });
                  }}
                >
                  <IconTrash />
                </Button>
              </Stack>
            ))}
            <Stack row justifyContent="flex-end">
              <Button
                variant="link-subtle"
                size="medium"
                onPress={() => {
                  onChange({
                    ...config,
                    select: {
                      ...config.select,
                      options: [
                        ...(config.select?.options ?? []),
                        { value: crypto.randomUUID(), label: { en: '', fr: '' } },
                      ],
                    },
                  });
                }}
              >
                <IconAdd />
                <Trans id="settings.organization.data-catalogs.condition-forms.detail.question.text-and-select.select-options.add-option">
                  Add option
                </Trans>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
